import React, { useState, useEffect } from 'react';
import { peoplePulseDataService } from '../utils/peoplePulseDataService';
import '../../../HR/components/styles/HRDashboard.css';
import '../styles/peoplepulse.css';

function TasksActionItems() {
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    assignedTo: '',
    dueDate: '',
    priority: 'medium',
    status: 'todo',
    relatedTo: '',
    relatedType: ''
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    priority: '',
    assignedTo: ''
  });

  const [viewMode, setViewMode] = useState('list'); // 'list' or 'board'

  useEffect(() => {
    loadTasks();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, filters]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await peoplePulseDataService.getTasks();
      setTasks(data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load tasks:', err);
      setError('Failed to load tasks: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tasks];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(task =>
        task.title?.toLowerCase().includes(searchLower) ||
        task.description?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(task => task.status === filters.status);
    }

    if (filters.priority) {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }

    if (filters.assignedTo) {
      filtered = filtered.filter(task => task.assignedTo === filters.assignedTo);
    }

    setFilteredTasks(filtered);
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      const newTask = await peoplePulseDataService.createTask(taskFormData);
      setTasks([newTask, ...tasks]);
      setShowCreateModal(false);
      resetForm();
      setSuccessMessage('Task created successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to create task: ' + err.message);
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const updated = await peoplePulseDataService.updateTask(selectedTask.id, taskFormData);
      setTasks(tasks.map(t => t.id === selectedTask.id ? updated : t));
      setShowEditModal(false);
      setSelectedTask(null);
      resetForm();
      setSuccessMessage('Task updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update task: ' + err.message);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const completed = await peoplePulseDataService.completeTask(taskId);
      setTasks(tasks.map(t => t.id === taskId ? completed : t));
      setSuccessMessage('Task completed!');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to complete task: ' + err.message);
    }
  };

  const handleQuickStatusUpdate = async (taskId, newStatus) => {
    try {
      await peoplePulseDataService.updateTask(taskId, { status: newStatus });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      setError('Failed to update status: ' + err.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await peoplePulseDataService.deleteTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
      setSuccessMessage('Task deleted');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to delete task: ' + err.message);
    }
  };

  const openEditModal = (task) => {
    setSelectedTask(task);
    setTaskFormData({
      title: task.title || '',
      description: task.description || '',
      assignedTo: task.assignedTo || '',
      dueDate: task.dueDate || '',
      priority: task.priority || 'medium',
      status: task.status || 'todo',
      relatedTo: task.relatedTo || '',
      relatedType: task.relatedType || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setTaskFormData({
      title: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      priority: 'medium',
      status: 'todo',
      relatedTo: '',
      relatedType: ''
    });
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': '#10B981',
      'medium': '#3B82F6',
      'high': '#F59E0B',
      'urgent': '#EF4444'
    };
    return colors[priority] || '#6C757D';
  };

  const getStatusColor = (status) => {
    const colors = {
      'todo': '#6C757D',
      'in-progress': '#3B82F6',
      'blocked': '#EF4444',
      'completed': '#10B981'
    };
    return colors[status] || '#6C757D';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getTasksByStatus = (status) => {
    return filteredTasks.filter(task => task.status === status);
  };

  if (loading) {
    return (
      <div className="hr-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading tasks...</p>
      </div>
    );
  }

  return (
    <div className="hr-dashboard-container peoplepulse-page">
      <div className="hr-dashboard-header">
        <div>
          <h1>Tasks & Action Items</h1>
          <p className="subtitle">{filteredTasks.length} task(s)</p>
        </div>
        <div className="header-actions">
          <div className="view-toggle">
            <button
              className={`btn-toggle ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={`btn-toggle ${viewMode === 'board' ? 'active' : ''}`}
              onClick={() => setViewMode('board')}
            >
              Board
            </button>
          </div>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            + Create Task
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search tasks..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />

        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="todo">To Do</option>
          <option value="in-progress">In Progress</option>
          <option value="blocked">Blocked</option>
          <option value="completed">Completed</option>
        </select>

        <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
          <option value="">All Priorities</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>

        <button className="btn-secondary" onClick={() => setFilters({ search: '', status: '', priority: '', assignedTo: '' })}>
          Clear Filters
        </button>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <div className="tasks-list">
          {filteredTasks.length === 0 ? (
            <div className="empty-state">
              <p>No tasks found</p>
              <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                Create Your First Task
              </button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr key={task.id} className={isOverdue(task.dueDate) && task.status !== 'completed' ? 'overdue-row' : ''}>
                    <td>
                      <div className="task-title-cell">
                        <strong>{task.title}</strong>
                        {task.description && <small>{task.description.substring(0, 60)}...</small>}
                      </div>
                    </td>
                    <td>{task.assignedToName || 'Unassigned'}</td>
                    <td>
                      <span className={isOverdue(task.dueDate) && task.status !== 'completed' ? 'overdue-date' : ''}>
                        {formatDate(task.dueDate)}
                        {isOverdue(task.dueDate) && task.status !== 'completed' && ' ⚠️'}
                      </span>
                    </td>
                    <td>
                      <span className="badge" style={{ backgroundColor: getPriorityColor(task.priority) }}>
                        {task.priority}
                      </span>
                    </td>
                    <td>
                      <select
                        value={task.status}
                        onChange={(e) => handleQuickStatusUpdate(task.id, e.target.value)}
                        className="status-select"
                        style={{ color: getStatusColor(task.status) }}
                      >
                        <option value="todo">To Do</option>
                        <option value="in-progress">In Progress</option>
                        <option value="blocked">Blocked</option>
                        <option value="completed">Completed</option>
                      </select>
                    </td>
                    <td>
                      <div className="action-buttons-cell">
                        {task.status !== 'completed' && (
                          <button className="btn-link small" onClick={() => handleCompleteTask(task.id)}>
                            ✓ Complete
                          </button>
                        )}
                        <button className="btn-link small" onClick={() => openEditModal(task)}>
                          Edit
                        </button>
                        <button className="btn-link small text-danger" onClick={() => handleDeleteTask(task.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Board View */}
      {viewMode === 'board' && (
        <div className="tasks-board">
          {['todo', 'in-progress', 'blocked', 'completed'].map((status) => (
            <div key={status} className="board-column">
              <div className="column-header">
                <h3>{status.replace('-', ' ').toUpperCase()}</h3>
                <span className="count">{getTasksByStatus(status).length}</span>
              </div>

              <div className="tasks-in-column">
                {getTasksByStatus(status).length === 0 ? (
                  <p className="no-tasks">No tasks</p>
                ) : (
                  getTasksByStatus(status).map((task) => (
                    <div key={task.id} className="task-board-card">
                      <div className="task-card-header">
                        <h4>{task.title}</h4>
                        <span className="badge small" style={{ backgroundColor: getPriorityColor(task.priority) }}>
                          {task.priority}
                        </span>
                      </div>

                      {task.description && (
                        <p className="task-description">{task.description}</p>
                      )}

                      <div className="task-card-footer">
                        <span className={isOverdue(task.dueDate) && task.status !== 'completed' ? 'overdue-date' : ''}>
                          {formatDate(task.dueDate)}
                          {isOverdue(task.dueDate) && task.status !== 'completed' && ' ⚠️'}
                        </span>
                        <span>{task.assignedToName || 'Unassigned'}</span>
                      </div>

                      <div className="task-card-actions">
                        {task.status !== 'completed' && (
                          <button className="btn-link small" onClick={() => handleCompleteTask(task.id)}>
                            Complete
                          </button>
                        )}
                        <button className="btn-link small" onClick={() => openEditModal(task)}>
                          Edit
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Task</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleCreateTask}>
              <div className="form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  value={taskFormData.title}
                  onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  required
                  placeholder="Enter task title"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  rows="3"
                  placeholder="Add task details..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    value={taskFormData.priority}
                    onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={taskFormData.status}
                    onChange={(e) => setTaskFormData({ ...taskFormData, status: e.target.value })}
                    required
                  >
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Assigned To</label>
                  <input
                    type="text"
                    value={taskFormData.assignedTo}
                    onChange={(e) => setTaskFormData({ ...taskFormData, assignedTo: e.target.value })}
                    placeholder="Enter name or email"
                  />
                </div>

                <div className="form-group">
                  <label>Due Date *</label>
                  <input
                    type="date"
                    value={taskFormData.dueDate}
                    onChange={(e) => setTaskFormData({ ...taskFormData, dueDate: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Related To</label>
                  <select
                    value={taskFormData.relatedType}
                    onChange={(e) => setTaskFormData({ ...taskFormData, relatedType: e.target.value })}
                  >
                    <option value="">None</option>
                    <option value="goal">Goal</option>
                    <option value="review">Performance Review</option>
                    <option value="meeting">1-on-1 Meeting</option>
                  </select>
                </div>

                {taskFormData.relatedType && (
                  <div className="form-group">
                    <label>Related Item</label>
                    <input
                      type="text"
                      value={taskFormData.relatedTo}
                      onChange={(e) => setTaskFormData({ ...taskFormData, relatedTo: e.target.value })}
                      placeholder="Enter item ID or name"
                    />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Task Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Task</h2>
              <button onClick={() => { setShowEditModal(false); setSelectedTask(null); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleUpdateTask}>
              <div className="form-group">
                <label>Task Title *</label>
                <input
                  type="text"
                  value={taskFormData.title}
                  onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select
                    value={taskFormData.priority}
                    onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Status *</label>
                  <select
                    value={taskFormData.status}
                    onChange={(e) => setTaskFormData({ ...taskFormData, status: e.target.value })}
                    required
                  >
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Assigned To</label>
                  <input
                    type="text"
                    value={taskFormData.assignedTo}
                    onChange={(e) => setTaskFormData({ ...taskFormData, assignedTo: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Due Date *</label>
                  <input
                    type="date"
                    value={taskFormData.dueDate}
                    onChange={(e) => setTaskFormData({ ...taskFormData, dueDate: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowEditModal(false); setSelectedTask(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TasksActionItems;


